/**
 * Keyboard focus target helpers for Reading/Listening MCQ question panels.
 *
 * @module mcqKeyboardNavigation
 */

import type { Question } from '@/types/exam-simulation';

export type McqFocusTarget =
  | { readonly kind: 'question'; readonly questionId: number }
  | { readonly kind: 'option'; readonly questionId: number; readonly optionId: string };

/**
 * Stable DOM id for a focus target (used with data-mcq-focus-id).
 */
export function getMcqFocusDomId(target: McqFocusTarget): string {
  if (target.kind === 'question') {
    return `question-${target.questionId}`;
  }
  return `option-${target.questionId}-${target.optionId}`;
}

/**
 * Build ordered focus targets: question card, then each non-struck option, per question.
 */
export function buildMcqFocusTargets(
  questions: readonly Question[],
  struckAnswers: Record<number, string[]>,
): McqFocusTarget[] {
  const targets: McqFocusTarget[] = [];
  for (const question of questions) {
    targets.push({ kind: 'question', questionId: question.id });
    if (question.type === 'matching') {
      continue;
    }
    const struck = struckAnswers[question.id] ?? [];
    for (const option of question.options) {
      if (!struck.includes(option.id)) {
        targets.push({ kind: 'option', questionId: question.id, optionId: option.id });
      }
    }
  }
  return targets;
}

/**
 * Index of the question-card target for a question id, or -1.
 */
export function findQuestionFocusIndex(
  targets: readonly McqFocusTarget[],
  questionId: number,
): number {
  return targets.findIndex((t) => t.kind === 'question' && t.questionId === questionId);
}

/**
 * Index of a focus target by DOM id, or -1.
 */
export function findFocusIndexByDomId(
  targets: readonly McqFocusTarget[],
  domId: string,
): number {
  return targets.findIndex((t) => getMcqFocusDomId(t) === domId);
}

/**
 * Advance focus index with wrap (Tab / Shift+Tab).
 */
export function stepFocusIndex(current: number, delta: 1 | -1, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return (current + delta + length) % length;
}

/**
 * Question id for the target at the given index (question or option row).
 */
export function getQuestionIdForFocusTarget(
  targets: readonly McqFocusTarget[],
  index: number,
): number | null {
  const target = targets[index];
  if (!target) {
    return null;
  }
  return target.questionId;
}
